/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: Detailed view of a component's design, code, props, and guidelines
 * Style referenceFiles:
 * Design for: Desktop
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - View component details via Tabs (Design, Code, Props, Guidelines)
 * - Edit component info, Archive
 * - Navigate to: ComponentPreview, VersionHistory, ComponentList
 * </page-design>
 */

import { useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { LayoutSidebar } from '@/components/function/LayoutSidebar'
import * as ComponentDetailStore from '@/stores/ComponentDetailStore'
import { pageLinks } from '@/pageLinks'

const statusStyles: Record<string, string> = {
  published: 'bg-emerald-50 text-emerald-600',
  draft: 'bg-amber-50 text-amber-600',
  review: 'bg-sky-50 text-sky-600',
  archived: 'bg-gray-100 text-gray-500',
}
const statusLabels: Record<string, string> = {
  published: '已发布', draft: '草稿', review: '审核中', archived: '已归档',
}

const tabs = [
  { key: 'design' as const, label: '设计稿', icon: 'lucide:image' },
  { key: 'code' as const, label: '代码', icon: 'lucide:code-2' },
  { key: 'props' as const, label: 'Props', icon: 'lucide:list' },
  { key: 'guidelines' as const, label: '使用指南', icon: 'lucide:book-open' },
]

function TabContent() {
  const activeTab = useAtomValue(ComponentDetailStore.activeTabAtom)
  const { data } = useAtomValue(ComponentDetailStore.refreshAtom)
  if (!data) return null
  const c = data.component

  if (activeTab === 'design') {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl">
        {c.thumbnailUrl ? <img src={c.thumbnailUrl} alt={c.name} className="max-w-full max-h-[600px] object-contain rounded-xl" /> : (
          <div className="text-center text-gray-400">
            <Icon icon="lucide:image" className="text-6xl mx-auto mb-4" />
            <p>暂无设计稿预览</p>
          </div>
        )}
      </div>
    )
  }
  if (activeTab === 'code') {
    return (
      <div className="bg-gray-900 text-gray-100 rounded-2xl p-6 overflow-x-auto">
        <pre className="text-sm font-mono whitespace-pre-wrap">{c.codeSnippet || '暂无代码片段'}</pre>
      </div>
    )
  }
  if (activeTab === 'props') {
    if (!c.propsDoc) return <div className="text-center py-16 text-gray-400">暂无 Props 文档</div>
    return (
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
        <pre className="text-sm whitespace-pre-wrap font-mono">{c.propsDoc}</pre>
      </div>
    )
  }
  if (activeTab === 'guidelines') {
    if (!c.guidelines) return <div className="text-center py-16 text-gray-400">暂无使用指南</div>
    return (
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
        <div className="prose prose-sm max-w-none whitespace-pre-wrap">{c.guidelines}</div>
      </div>
    )
  }
  return null
}

function DetailSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="h-10 bg-gray-100 rounded-xl w-1/3" />
      <div className="h-8 bg-gray-100 rounded-xl w-full" />
      <div className="h-64 bg-gray-100 rounded-2xl" />
    </div>
  )
}

export default function ComponentDetail() {
  const { data, isPending } = useAtomValue(ComponentDetailStore.refreshAtom)
  const activeTab = useAtomValue(ComponentDetailStore.activeTabAtom)
  const setTab = useSetAtom(ComponentDetailStore.activeTabAtom)
  const archive = useSetAtom(ComponentDetailStore.archiveAtom)
  const [showMenu, setShowMenu] = useState(false)

  return (
    <div className="flex h-screen w-full bg-[#F8F9FA] font-['Noto_Sans_SC',sans-serif] text-[#1A1D25] overflow-hidden">
      <LayoutSidebar />
      <div className="flex-1 flex flex-col h-full overflow-hidden p-6 pb-0">
        {isPending ? <DetailSkeleton /> : data && (
          <>
            <header className="flex items-center justify-between mb-6 px-2">
              <div className="flex items-center gap-3">
                <Link to={pageLinks.ComponentList()} className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-gray-600 shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all"><Icon icon="lucide:arrow-left" className="text-xl" /></Link>
                <h1 className="text-2xl font-bold tracking-tight">{data.component.name}</h1>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${statusStyles[data.component.status] || ''}`}>{statusLabels[data.component.status] || data.component.status}</span>
                <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-1 rounded-lg">{data.component.currentVersion}</span>
              </div>
              <div className="flex items-center gap-3">
                <Link to={pageLinks.ComponentPreview(data.component.id)} className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-white shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all flex items-center gap-2"><Icon icon="lucide:play" />预览</Link>
                <Link to={pageLinks.VersionHistory(data.component.id)} className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-white shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all flex items-center gap-2"><Icon icon="lucide:history" />版本</Link>
                <div className="relative">
                  <button onClick={() => setShowMenu(!showMenu)} className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-gray-600 shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all"><Icon icon="lucide:more-horizontal" className="text-xl" /></button>
                  {showMenu && (
                    <div className="absolute right-0 top-12 bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.1)] py-2 w-40 z-10">
                      <button onClick={() => { void archive(); setShowMenu(false) }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 text-red-600 flex items-center gap-2"><Icon icon="lucide:archive" />归档组件</button>
                    </div>
                  )}
                </div>
              </div>
            </header>

            <div className="flex gap-1 mb-6 px-2">
              {tabs.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${activeTab === t.key ? 'bg-white text-[#1D263A] shadow-[0_2px_10px_rgb(0,0,0,0.04)]' : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'}`}>
                  <Icon icon={t.icon} className="text-lg" />{t.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto pb-8 px-2">
              <TabContent />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
